import { Menu, MenuItem } from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Accordion from '../../../../components/Accordion/Accordion';
import Button from '../../../../components/Button/Button';
import Input from '../../../../components/Input/Input';
import Spinner from '../../../../components/Spinner/Spinner';
import { useAppDispatch, useAppSelector } from '../../../../reduxService/hooks';
import { ITemplateRequest } from '../../../api/IApiRequest';
import * as BeMaccaferriStoreHelper from '../../../api/store/BeMaccaferriStoreHelper';
import Layout from '../../../components/Layout/Layout';
import isAdminUser from '../../../hoc/isAdminUser';
import { ELanguages, TLanguages, languages } from '../../../localization';
import { endpoint } from '../../../pages';
import Geometry from '../../components/InputForm/Geometry/Geometry';
import Materials from '../../components/InputForm/Materials/Materials';
import ChannelDesign from '../../components/graphicalResults/channelDesign/ChannelDesign';
import { selectProject } from '../../selector/conversion';
import { cleanProject, fetchTemplateById } from '../../store/project/projectSlice';
import './BackofficeTemplate.scss';
import { IBackofficeTemplateProps } from './IBackofficeTemplate';
import { addIdToFile } from '../../../utils/utils';
import { toastSuccess, warning } from '../../../../components/Alert/Alert';
import HydraulicData from '../../components/InputForm/HydraulicData/HydraulicData';

const BackofficeTemplate = (props: IBackofficeTemplateProps) => {
    const { t, i18n } = useTranslation();
    const project = useAppSelector(selectProject);
    const dispatch = useAppDispatch();
    const { id } = useParams();
    const navigate = useNavigate();

    const defaultTemplate: any = {
        infos: [{ languageCodeISO: 'en', name: '' }],
        isDeleted: false,
        sectors: [],
    };
    const [template, setTemplate] = React.useState<ITemplateRequest>(defaultTemplate);
    const inputImage = React.useRef<HTMLInputElement | null>(null);
    const languagesAvailable = Object.keys(ELanguages).filter(l => !template.infos.find(el => el.languageCodeISO === l));
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [imgUrl, setImgUrl] = React.useState<string>('');

    const open = Boolean(anchorEl);

    React.useEffect(() => {
        const fetchTemplate = async () => {
            if (!!id) {
                dispatch(fetchTemplateById({ id: parseInt(id) }));
                const templateWithLanguages = await BeMaccaferriStoreHelper.getCBPWithAllLanguagesAsync(parseInt(id));
                setImgUrl(templateWithLanguages.data.imageUrl);
                setTemplate(templateWithLanguages.data);
            } else {
                const templates = await BeMaccaferriStoreHelper.getCBPPreviewTemplatesAsync(1, 1);
                dispatch(fetchTemplateById({ id: templates.data.data[0].id }));
                const res = await BeMaccaferriStoreHelper.getTemplateCBPByIdAsync(templates.data.data[0].id);
                res.data.infos = defaultTemplate.infos;
                res.data.imageUrl = '';
                res.data.name = '';
                setTemplate(res.data);
            }
        };
        fetchTemplate();
    }, []);

    const handleInputName = (event: React.ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        const name = target.name;
        const newInfos = template.infos.map(info => {
            if (info.languageCodeISO === name) info.name = target.value;
            return info;
        });
        setTemplate({
            ...template,
            infos: newInfos,
        });
    };

    const handleClickMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(e.currentTarget);
    };
    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const addLanguage = (l: string) => {
        setTemplate({
            ...template,
            infos: [...template.infos, { languageCodeISO: l, name: '' }],
        });
    };

    const handleUploadLocalImage = () => {
        inputImage.current?.click();
    };

    const handleChangeUploadLocalImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e?.target?.files && e?.target?.files[0]) {
            let original = e.target.files[0];
            let filename = addIdToFile(original.name);
            const img = new File([original], filename, {
                type: original.type,
                lastModified: original.lastModified,
            });

            setImgUrl(URL.createObjectURL(img));
            setTemplate({
                ...template,
                image: img,
            });
        }
    };

    const handleCancel = () => {
        navigate(endpoint.backoffice);
    };

    const handleSave = async () => {
        try {
            if (!!project.data?.geometry) {
                let templateCompleted: ITemplateRequest = { ...template, sectors: project.data.geometry, hydraulicData: project.data.hydraulicData! };
                if (!!id) {
                    await BeMaccaferriStoreHelper.updateCBPTemplateAsync(templateCompleted);
                } else {
                    await BeMaccaferriStoreHelper.addCBPTemplateAsync(templateCompleted);
                }
            }
            dispatch(cleanProject());
            navigate(endpoint.backoffice);
            toastSuccess(t('header_projects_saved'));
        } catch (err) {
            warning(t('bo_material_toast_error_update'));
        }
    };

    return (
        <Layout>
            {!project.initialState && !project.pending ? (
                <div className='d-flex p-2 gap-2 h-85'>
                    <div className='w-25 d-flex flex-column gap-2 overflow-auto'>
                        <label className='py-3'>{t('backoffice_general_info')}</label>
                        {template.infos.map(info => {
                            let x = info.languageCodeISO as unknown as TLanguages;
                            const language = languages[x];
                            return (
                                <>
                                    {!!language && (
                                        <Accordion title={language} key={info.languageCodeISO}>
                                            <Input type='text' title='Name' name={info.languageCodeISO} className='d-flex flex-column' value={info?.name} onChange={handleInputName} />
                                            <Button
                                                className='delete-btn-wrapper'
                                                type='button'
                                                onClick={() => setTemplate(value => ({ ...value, infos: value.infos.filter(el => el.languageCodeISO !== x) }))}>
                                                <i className='fa-solid fa-trash icon-delete' />
                                            </Button>
                                        </Accordion>
                                    )}
                                </>
                            );
                        })}

                        <Button className='d-flex justify-content-end pt-2' classNameBtn='btn-color-inverted' onClick={handleClickMenu}>
                            <i className='fa-solid fa-plus' />
                        </Button>
                        <Menu
                            id='basic-menu-bo-template'
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleCloseMenu}
                            MenuListProps={{
                                'aria-labelledby': 'basic-button',
                            }}>
                            <>
                                {languagesAvailable.map(l => (
                                    <MenuItem key={l} onClick={() => addLanguage(l)}>
                                        {languages[l as TLanguages]}
                                    </MenuItem>
                                ))}
                            </>
                        </Menu>
                    </div>
                    <div className='w-25 d-flex flex-column gap-2 overflow-auto'>
                        <label className='py-3'>{t('backoffice_data')}</label>
                        <Accordion title={t('input_form_accordion_hydraulic_data')}>
                            <HydraulicData />
                        </Accordion>
                        <Accordion title={t('input_form_accordion_geometry')}>
                            <Geometry />
                        </Accordion>
                        <Accordion title={t('input_form_accordion_materials')}>{!project.initialState && !project.pending && <Materials />}</Accordion>
                        <Accordion title={t('backoffice_template_picture')}>
                            <div className='d-flex justify-content-around pictureBox'>
                                <Button classNameBtn='btn-color-inverted' onClick={handleUploadLocalImage}>
                                    <i className='fa-solid fa-arrow-up-from-bracket' />
                                    <input className='d-none' type='file' ref={inputImage} onChange={handleChangeUploadLocalImage} />
                                </Button>
                                {imgUrl && <img src={imgUrl} alt={t('backoffice_template_picture_alt') as string} className='w-75 h-75' />}
                            </div>
                        </Accordion>
                    </div>
                    <div className='w-50'>
                        <label className='py-3'>{t('backoffice_board')}</label>
                        {!project.initialState && !project.pending ? <ChannelDesign /> : <Spinner />}
                        <div className='d-flex justify-content-end gap-3 mt-3'>
                            <Button classNameBtn='btn-color-inverted' onClick={handleCancel}>
                                {t('cancel_button')}
                            </Button>
                            <Button onClick={handleSave}>{t('header_save_button')}</Button>
                        </div>
                    </div>
                </div>
            ) : (
                <Spinner />
            )}
        </Layout>
    );
};

export default isAdminUser(BackofficeTemplate);
